import React, { useState, useEffect, useRef } from 'react';
import { List, RefreshCw, Plus } from 'lucide-react';
import { AIAgentList, getAgentLists, createAgentList } from '../../lib/aiAgents';
import AgentListCard from './AgentListCard';
import toast from 'react-hot-toast';

interface AgentListsPanelProps {
  agentId: string;
  refreshTrigger: number;
}

export default function AgentListsPanel({ agentId, refreshTrigger }: AgentListsPanelProps) {
  const [lists, setLists] = useState<AIAgentList[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newListIds, setNewListIds] = useState<Set<string>>(new Set());
  const prevIdsRef = useRef<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);

  const loadLists = async () => {
    try {
      const data = await getAgentLists(agentId);

      const currentIds = new Set(data.map(l => l.id));
      const prevIds = prevIdsRef.current;
      const freshIds = new Set<string>();
      currentIds.forEach(id => {
        if (!prevIds.has(id)) freshIds.add(id);
      });
      prevIdsRef.current = currentIds;

      if (freshIds.size > 0) {
        setNewListIds(freshIds);
        setTimeout(() => setNewListIds(new Set()), 3000);
      }

      setLists(data);
    } catch (err) {
      console.error('Failed to load lists:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLists();
  }, [agentId, refreshTrigger]);

  useEffect(() => {
    if (showCreateInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showCreateInput]);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      await createAgentList(agentId, newTitle.trim());
      setNewTitle('');
      setShowCreateInput(false);
      await loadLists();
      toast.success('List created');
    } catch {
      toast.error('Failed to create list');
    } finally {
      setCreating(false);
    }
  };

  const activeCount = lists.filter(l => l.is_active).length;

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <List className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-900">Lists</span>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
            {activeCount}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowCreateInput(true)}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            title="New list"
          >
            <Plus className="h-3.5 w-3.5 text-gray-500" />
          </button>
          <button
            onClick={loadLists}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="h-3.5 w-3.5 text-gray-400" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {/* Create Input */}
        {showCreateInput && (
          <div className="flex gap-1.5 items-center mb-2">
            <input
              ref={inputRef}
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="List title (e.g. Labor Rates)"
              className="flex-1 text-xs border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
                if (e.key === 'Escape') { setShowCreateInput(false); setNewTitle(''); }
              }}
              disabled={creating}
            />
            <button
              onClick={handleCreate}
              disabled={!newTitle.trim() || creating}
              className="px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              Add
            </button>
            <button
              onClick={() => { setShowCreateInput(false); setNewTitle(''); }}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <span className="text-xs">Cancel</span>
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : lists.length === 0 && !showCreateInput ? (
          <div className="text-center py-8">
            <List className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No lists yet</p>
            <p className="text-xs text-gray-400 mt-1">Add reference data your agent always needs</p>
            <button
              onClick={() => setShowCreateInput(true)}
              className="mt-3 text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              + Create first list
            </button>
          </div>
        ) : (
          lists.map(list => (
            <AgentListCard
              key={list.id}
              list={list}
              isNew={newListIds.has(list.id)}
              onUpdated={loadLists}
              onDeleted={loadLists}
            />
          ))
        )}
      </div>
    </div>
  );
}
